import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LoginIcon from "@mui/icons-material/Login";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTheme, alpha } from "@mui/material/styles";
import PrescriptionEval from "../Exercise/PrescriptionEval";
import {
  adminSurfaceSx,
  adminIconBadgeSx,
  adminPageSx,
  adminToggleGroupSx,
  ADMIN_ACCENTS,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import AdminSectionHeader from "./AdminSectionHeader";
import { adminRequest } from "../../utils/adminApi";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const WINDOW_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

// Draw from the single shared admin palette so these lines match the rest of
// the console rather than re-declaring near-duplicate hexes locally.
const SIGNUP_COLOR = ADMIN_ACCENTS[1]; // teal
const LOGIN_COLOR = ADMIN_ACCENTS[0]; // blue

const SIGNUP_TYPE = "user_signup";
const LOGIN_TYPE = "user_login";

// The /admin/usage page. Reads the live D1-backed events endpoint
// (GET /api/admin/events via the functions/api/admin/getEvents proxy) and
// presents a focused Signups & Logins view: totals + a daily over-time chart.
// This replaces the old Analytics Engine read path (worker
// /api/admin/user-analytics), which was removed in worker PR #104.
//
// IMPORTANT: on getEventsAdminRoute the `type` param only narrows the raw
// `events` feed — the `countsByType` and `daily` aggregates are always GLOBAL
// (every event_type) so /admin/events can render its full breakdown. So we make
// ONE unfiltered call and pivot the windowed `daily` series by event_type
// ourselves. (Summing a per-"type" call's global countsByType was the earlier
// bug where signups and logins both showed the all-events total.)
export default function AnalyticsDashboard() {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token + routes via ?env=staging (adminRequest).
  const { env, token, envParams } = useSelector(adminRequest);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  // Phase 9 (doc 29, P9.9): movement to inspect prescription source/reasoning +
  // override-rate for. Free-text so an admin can probe any movement they log.
  const [movementInput, setMovementInput] = useState("");
  const [inspectedMovement, setInspectedMovement] = useState(null);

  useEffect(() => {
    // Skip the fetch in staging mode until a staging token is held (see
    // EventsDashboard) — the AdminLayout login dialog handles auth.
    if (env === "staging" && !token) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchUsage = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get("/api/admin/getEvents", {
          params: { days, ...envParams },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Failed to fetch usage analytics:", err);
        if (!cancelled) setError("Failed to load usage analytics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUsage();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, token, days]);

  // Pivot the global daily series ({ day, event_type, count }) — windowed to the
  // trailing `days` — into a signups dataset and a logins dataset over one shared
  // day axis, and derive each type's windowed total from those same rows so the
  // totals always agree with the chart and the 7/30/90 toggle.
  const { chart, signupTotal, loginTotal } = useMemo(() => {
    const daily = data?.daily ?? [];
    const dayset = new Set();
    const signupsByDay = new Map();
    const loginsByDay = new Map();
    let signups = 0;
    let logins = 0;
    for (const row of daily) {
      const count = row.count ?? 0;
      if (row.event_type === SIGNUP_TYPE) {
        dayset.add(row.day);
        signupsByDay.set(row.day, count);
        signups += count;
      } else if (row.event_type === LOGIN_TYPE) {
        dayset.add(row.day);
        loginsByDay.set(row.day, count);
        logins += count;
      }
    }
    const labels = Array.from(dayset).sort();
    return {
      signupTotal: signups,
      loginTotal: logins,
      chart: {
        labels,
        datasets: [
          {
            label: "Signups",
            data: labels.map((day) => signupsByDay.get(day) ?? 0),
            borderColor: SIGNUP_COLOR,
            backgroundColor: SIGNUP_COLOR,
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: "Logins",
            data: labels.map((day) => loginsByDay.get(day) ?? 0),
            borderColor: LOGIN_COLOR,
            backgroundColor: LOGIN_COLOR,
            tension: 0.3,
            pointRadius: 2,
          },
        ],
      },
    };
  }, [data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { labels: { color: theme.palette.text.secondary } },
      },
      scales: {
        x: {
          ticks: { color: theme.palette.text.secondary },
          grid: { color: alpha("#ffffff", 0.06) },
        },
        y: {
          beginAtZero: true,
          ticks: { color: theme.palette.text.secondary, precision: 0 },
          grid: { color: alpha("#ffffff", 0.06) },
        },
      },
    }),
    [theme]
  );

  const totals = [
    {
      key: "signups",
      title: "Total Signups",
      icon: <PersonAddIcon />,
      accent: SIGNUP_COLOR,
      count: signupTotal,
      testid: "usage-signups-total",
    },
    {
      key: "logins",
      title: "Total Logins",
      icon: <LoginIcon />,
      accent: LOGIN_COLOR,
      count: loginTotal,
      testid: "usage-logins-total",
    },
  ];

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<TimelineIcon />}
          title="Signups &amp; Logins"
          subtitle={`User signups and logins over the last ${days} days`}
          actions={
            <ToggleButtonGroup
              value={days}
              exclusive
              size="small"
              onChange={(_e, v) => v && setDays(v)}
              sx={adminToggleGroupSx(theme)}
            >
              {WINDOW_OPTIONS.map((opt) => (
                <ToggleButton key={opt.days} value={opt.days}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {totals.map((stat) => (
                <Grid size={{ xs: 12, sm: 6 }} key={stat.key}>
                  <Card sx={adminSurfaceSx(theme, { accent: stat.accent })}>
                    <CardContent>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Box sx={adminIconBadgeSx(stat.accent)}>
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="h4"
                          data-testid={stat.testid}
                          sx={{ fontWeight: 700, color: stat.accent }}
                        >
                          {stat.count.toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        {stat.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Card sx={{ ...adminSurfaceSx(theme), mb: 3 }}>
              <CardContent>
                <AdminSectionHeader
                  label="Daily signups vs logins"
                  accent={SIGNUP_COLOR}
                />
                <Box sx={{ height: 320 }}>
                  {chart.labels.length > 0 ? (
                    <Line data={chart} options={chartOptions} />
                  ) : (
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                      No signup or login activity in this window yet.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Phase 9 (doc 29, P9.9): progressive-overload eval surfacing.
                Per movement: prescription source + reasoning, and the
                override-rate trust metric (doc 05). Independent of the events
                feed — kept here as an admin probe. */}
            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <AdminSectionHeader
                  label="Progression Eval"
                  accent={ADMIN_ACCENTS[4]}
                  divider={false}
                  sx={{ mb: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mb: 2 }}
                >
                  Inspect a movement: whether its next session was prescribed by
                  the progressive-overload engine, the engine&apos;s reasoning,
                  and the override rate (how often the prescribed numbers were
                  overridden).
                </Typography>
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const trimmed = movementInput.trim();
                    if (trimmed) setInspectedMovement(trimmed);
                  }}
                  sx={{ display: "flex", gap: 1, mb: 2, maxWidth: 480 }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    label="Movement name (e.g. Squat)"
                    value={movementInput}
                    onChange={(e) => setMovementInput(e.target.value)}
                  />
                  <Button type="submit" variant="contained">
                    Inspect
                  </Button>
                </Box>
                {inspectedMovement ? (
                  <Box sx={{ maxWidth: 520 }}>
                    <PrescriptionEval exerciseName={inspectedMovement} />
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}
